import { useCallback, useMemo, useRef, useState } from 'react';

import { Table } from '@tanstack/react-table';

import { Currency } from '@/modules/common';

import {
  getBillMemberPath,
  getBillMemberRowId,
  getBillSubRowId,
} from '../../components/generate-bills-table/bill-row.helpers';
import {
  AddedProjectActions,
  OverheadActions,
} from '../../components/generate-bills-table/generate-bills-table.types';
import {
  GenerateBillAddedProjectListItem,
  GenerateBillListItem,
  GenerateBillMemberOverheadListItem,
  GenerateBillSubRowType,
} from '../../types';

interface UseBillCustomRowsParams {
  /**
   * Drops a removed row's field group from the form.
   *
   * Taken as a callback rather than the form itself so this hook can be declared *before*
   * `useGenerateEntityPage` — its `hasCustomRows` flag has to feed that hook's
   * `additionalIsDirty`, which is read during render and therefore cannot be deferred.
   */
  unregisterFields: (path: string) => void;
}

export interface BillCustomRows {
  memberOverheads: Record<string, GenerateBillMemberOverheadListItem[]>;
  memberAddedProjects: Record<string, GenerateBillAddedProjectListItem[]>;
  overheadActions: OverheadActions;
  addedProjectActions: AddedProjectActions;
  /** True when the user has added at least one custom row (feeds the unsaved-changes guard). */
  hasCustomRows: boolean;
  reset: () => void;
  /**
   * Selects rows added since the last render. Call from an effect keyed on the composed table
   * data — a new row does not exist in TanStack's model until that data rebuilds.
   */
  flushPendingSelections: (table: Table<GenerateBillListItem>) => void;
}

/**
 * Owns the user-added overhead and project rows.
 *
 * These live outside React Hook Form because RHF cannot track row creation/deletion — the form
 * only holds the *fields* of a row that already exists. This hook is the source of truth for
 * which custom rows exist; the form owns their values.
 */
export const useBillCustomRows = ({
  unregisterFields,
}: UseBillCustomRowsParams): BillCustomRows => {
  const [memberOverheads, setMemberOverheads] = useState<
    Record<string, GenerateBillMemberOverheadListItem[]>
  >({});
  const [memberAddedProjects, setMemberAddedProjects] = useState<
    Record<string, GenerateBillAddedProjectListItem[]>
  >({});

  const pendingSelections = useRef<string[]>([]);

  const addOverhead = useCallback((memberId: string, currencyCode: string) => {
    const newItem: GenerateBillMemberOverheadListItem = {
      type: GenerateBillSubRowType.OVERHEAD,
      id: crypto.randomUUID(),
      description: '',
      effectiveRate: 0,
      currency: { code: currencyCode, displayName: '' } as Currency,
    };
    pendingSelections.current.push(getBillSubRowId(getBillMemberRowId(memberId), newItem));
    setMemberOverheads(prev => ({ ...prev, [memberId]: [...(prev[memberId] ?? []), newItem] }));
  }, []);

  const addProject = useCallback((memberId: string, currencyCode: string) => {
    const newItem: GenerateBillAddedProjectListItem = {
      type: GenerateBillSubRowType.ADDED_PROJECT,
      id: crypto.randomUUID(),
      projectId: null,
      projectName: '',
      logoUrl: '',
      colorHex: '',
      hours: 0,
      effectiveRate: 0,
      currency: { code: currencyCode, displayName: '' } as Currency,
      total: 0,
    };
    pendingSelections.current.push(getBillSubRowId(getBillMemberRowId(memberId), newItem));
    setMemberAddedProjects(prev => ({ ...prev, [memberId]: [...(prev[memberId] ?? []), newItem] }));
  }, []);

  // Fields are keyed by the row's own UUID, so removing a custom row just unregisters that row's
  // field group and drops it from state — no index re-shuffling of siblings needed.
  const removeOverhead = useCallback(
    (memberId: string, overheadId: string) => {
      unregisterFields(`${getBillMemberPath(Number(memberId))}.overhead.o${overheadId}`);
      setMemberOverheads(prev => dropRow(prev, memberId, item => item.id !== overheadId));
    },
    [unregisterFields]
  );

  const removeAddedProject = useCallback(
    (memberId: string, addedProjectId: string) => {
      unregisterFields(`${getBillMemberPath(Number(memberId))}.addedProject.a${addedProjectId}`);
      setMemberAddedProjects(prev => dropRow(prev, memberId, item => item.id !== addedProjectId));
    },
    [unregisterFields]
  );

  const flushPendingSelections = useCallback((table: Table<GenerateBillListItem>) => {
    const rowIds = pendingSelections.current;
    if (rowIds.length === 0) return;

    pendingSelections.current = [];
    table.setRowSelection(prev => ({
      ...prev,
      ...Object.fromEntries(rowIds.map(rowId => [rowId, true])),
    }));
  }, []);

  const reset = useCallback(() => {
    setMemberOverheads({});
    setMemberAddedProjects({});
  }, []);

  return {
    memberOverheads,
    memberAddedProjects,
    overheadActions: useMemo(
      () => ({ addOverhead, removeOverhead }),
      [addOverhead, removeOverhead]
    ),
    addedProjectActions: useMemo(
      () => ({ addProject, removeAddedProject }),
      [addProject, removeAddedProject]
    ),
    hasCustomRows:
      Object.keys(memberOverheads).length > 0 || Object.keys(memberAddedProjects).length > 0,
    reset,
    flushPendingSelections,
  };
};

/** Removes one row from a member's bucket, dropping the bucket entirely once it is empty. */
const dropRow = <TItem>(
  buckets: Record<string, TItem[]>,
  memberId: string,
  keep: (item: TItem) => boolean
): Record<string, TItem[]> => {
  const remaining = (buckets[memberId] ?? []).filter(keep);
  const next = { ...buckets, [memberId]: remaining };
  if (remaining.length === 0) delete next[memberId];
  return next;
};
